import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Home, LogOut, Settings, User, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import AdSense from './AdSense';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: t('navigation.home'), href: '/', icon: Home, showWhenLoggedIn: false },
    { name: t('navigation.about'), href: '/about', icon: User, showWhenLoggedIn: false },
    { name: t('navigation.dashboard'), href: '/dashboard', icon: Calendar, protected: true },
  ];

  const isActive = (path) => location.pathname === path;

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">EasyRDV</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              {navigation.map((item) => {
                if (item.protected && !user) return null;
                if (item.showWhenLoggedIn === false && user) return null;

                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive(item.href)
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* User menu */}
            <div className="flex items-center space-x-4">
              {/* Language Switcher */}
              <LanguageSwitcher />
              
              {user ? (
                <div className="flex items-center space-x-4">
                  {/* User name in center - hidden on mobile */}
                  <div className="hidden md:flex items-center space-x-2 text-sm text-gray-700">
                    <User className="w-4 h-4" />
                    <span className="font-medium">
                      {user.name} <small><i>@{user.username}</i></small>
                    </span>
                  </div>
                  {/* Logout button on the right with red color */}
                  <button
                    onClick={logout}
                    className="hidden md:flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors border border-red-200 hover:border-red-300"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{t('navigation.logout')}</span>
                  </button>
                </div>
              ) : (
                <div className="hidden md:flex items-center space-x-4">
                  <Link
                    to="/login"
                    className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    {t('navigation.login')}
                  </Link>
                  <Link
                    to="/register"
                    className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    {t('navigation.register')}
                  </Link>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 md:hidden"
            onClick={closeSidebar}
          />
          
          {/* Sidebar */}
          <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out md:hidden">
            <div className="flex items-center justify-between h-16 px-4 border-b">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-gray-900">EasyRDV</span>
              </div>
              <button
                onClick={closeSidebar}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Mobile Navigation */}
            <nav className="px-4 py-6 space-y-2">
              {navigation.map((item) => {
                if (item.protected && !user) return null;
                if (item.showWhenLoggedIn === false && user) return null;

                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={closeSidebar}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                      isActive(item.href)
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Mobile User Section */}
            {user ? (
              <div className="px-4 py-6 border-t">
                <div className="flex items-center space-x-3 mb-4">
                  <User className="w-5 h-5 text-gray-400" />
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">{user.name}</div>
                    <div className="text-gray-500">@{user.username}</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    logout();
                    closeSidebar();
                  }}
                  className="flex items-center space-x-3 w-full px-3 py-3 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors border border-red-200 hover:border-red-300"
                >
                  <LogOut className="w-5 h-5" />
                  <span>{t('navigation.logout')}</span>
                </button>
              </div>
            ) : (
              <div className="px-4 py-6 border-t space-y-3">
                <Link
                  to="/login"
                  onClick={closeSidebar}
                  className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  {t('navigation.login')}
                </Link>
                <Link
                  to="/register"
                  onClick={closeSidebar}
                  className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  {t('navigation.register')}
                </Link>
              </div>
            )}
          </div>
        </>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Google AdSense Ad Bottom - Footer Banner */}
      <div className="w-full bg-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <AdSense 
              slot="7645681296"
              format="horizontal"
              style={{ display: 'block', width: '728px', height: '90px' }}
              className="w-full max-w-4xl"
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Company Info */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white">EasyRDV</span>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                {t('home.subtitle')}
              </p>
            </div>

            {/* Quick Links */}
            <div className="text-center">
              <h3 className="text-white font-semibold mb-4">Liens rapides</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><Link to="/" className="hover:text-blue-400 transition-colors">{t('navigation.home')}</Link></li>
                <li><Link to="/dashboard" className="hover:text-blue-400 transition-colors">{t('navigation.dashboard')}</Link></li>
                <li><Link to="/about" className="hover:text-blue-400 transition-colors">{t('navigation.about')}</Link></li>
                <li><Link to="/login" className="hover:text-blue-400 transition-colors">{t('navigation.login')}</Link></li>
                <li><Link to="/register" className="hover:text-blue-400 transition-colors">{t('navigation.register')}</Link></li>
              </ul>
            </div>

            {/* Contact & Version */}
            <div className="text-center md:text-right">
              <h3 className="text-white font-semibold mb-4">Informations</h3>
              <div className="space-y-2 text-sm text-gray-300">
                <p>Version 1.0.0</p>
                <p>© {new Date().getFullYear()} EasyRDV</p>
                <p className="text-xs text-gray-400 mt-4">
                  Développé avec ❤️ par{' '}
                  <a
                    href="https://github.com/yacinova"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 font-medium"
                  >
                    Yassine Hamdoune
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
              <p>{t('footer.copyright')}</p>
              <div className="flex flex-wrap justify-center md:justify-end space-x-4 md:space-x-6 mt-2 md:mt-0">
                <Link 
                  to="/legal/mentions-legales" 
                  className="hover:text-blue-400 transition-colors"
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: 'smooth'
                    });
                  }}
                >
                  {t('navigation.legal.mentions')}
                </Link>
                <Link 
                  to="/legal/politique-confidentialite" 
                  className="hover:text-blue-400 transition-colors" 
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: 'smooth'
                    });
                  }}
                >
                  {t('navigation.legal.privacy')}
                </Link>
                <Link 
                  to="/legal/conditions-utilisation" 
                  className="hover:text-blue-400 transition-colors" 
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: 'smooth'
                    });
                  }}
                >
                  {t('navigation.legal.terms')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout; 